import { FocusSchema, IdSchema, LocaleSchema, TimestampSchema } from "@uzverse/core";
import { z } from "zod";
import { VehiclePoseMessageSchema } from "./vehicles.js";
import { BuildingStyleSchema, CompanyBrandingSchema, FloorThemeSchema } from "./branding.js";
/**
 * Contracts of the world partition (`services/world`, docs/PHASE2_PLAN.md §2.1, §2.6).
 *
 * The world is a reserved partition: its tool host runs with `companyId = WORLD_PARTITION_ID`
 * (core `ToolContext.companyId` is not nullable), and world entities and events carry it as
 * their `companyId`. It is a constant ULID with time 0, so no generated id can ever equal it.
 */
export const WORLD_PARTITION_ID = "00000000000000000000W0R1D0";
/** True when `companyId` names the world partition rather than a company. */
export function isWorldPartition(companyId) {
    return companyId === WORLD_PARTITION_ID;
}
/** Audience of service tokens addressed to the world service (instead of a company id). */
export const WORLD_SERVICE_AUDIENCE = "world";
/** Audience of MCP principal tokens for the world service's `/mcp` endpoint. */
export const WORLD_MCP_AUDIENCE = "world";
/**
 * Label for deriving the world MCP token HMAC key from the service-token secret. It differs from
 * the company node's label, so a world token never verifies on a node's `/mcp` and vice versa.
 */
export const WORLD_MCP_TOKEN_LABEL = "uzverse/mcp-token/world";
/**
 * Claims of a world MCP principal token, minted by the company node for an agent run next to
 * its own MCP token. The token carries the task's focus so world tools see `ctx.focus`.
 */
export const WorldMcpClaimsSchema = z.object({
    kind: z.literal("mcp"),
    aud: z.literal(WORLD_MCP_AUDIENCE),
    agentId: IdSchema,
    onBehalfOf: IdSchema,
    /** The agent's company (where its role and the human's grants are evaluated). */
    companyId: IdSchema,
    taskId: IdSchema.nullable(),
    locale: LocaleSchema,
    focus: FocusSchema.nullable(),
    /** Expiry, epoch seconds. */
    exp: z.number().int(),
});
/**
 * Routes of `services/world`, called by control-plane services with a service token whose
 * audience is `WORLD_SERVICE_AUDIENCE` (MCP uses world MCP principal tokens instead).
 */
export const WORLD_SERVICE_API = {
    /** GET → `WorldSceneSnapshot`. */
    snapshot: "/v1/scene/snapshot",
    /**
     * WebSocket; query `afterSeq` (default 0). First client frame `{ "token" }` (as the node
     * streams). Server frames are `WorldStreamFrame`s; see `WorldSceneSource.stream`.
     */
    stream: "/v1/scene/stream",
    /** POST `ToolInvokeRequest` (from `NODE_EXTRA_API`) → core `ToolInvocationResult`. */
    toolInvoke: "/v1/tools/invoke",
    /** MCP Streamable HTTP endpoint for agent runs (world MCP principal tokens). */
    mcp: "/mcp",
    /** POST upload (package 7), GET list. */
    assets: "/v1/assets",
    /** GET the blob of an approved asset. */
    asset: "/v1/assets/:sha256",
};
/** Audience of service tokens addressed to the gateway by the world service (notices). */
export const GATEWAY_SERVICE_AUDIENCE = "gateway";
/** POST on the gateway (service token, audience `GATEWAY_SERVICE_AUDIENCE`): `WorldNotice`. */
export const GATEWAY_WORLD_NOTICE_PATH = "/internal/v1/world/notices";
/**
 * A message the world sends to the assistant chat of the human who asked for something. The
 * gateway renders the text in the reader's language and delivers it as a `chat.message`.
 */
export const WorldNoticeSchema = z.object({
    kind: z.literal("placement_rejected"),
    /** The human the requesting assistant works for. */
    userId: IdSchema,
    companyId: IdSchema,
    /** The task the request came from (the chat thread), when known. */
    taskId: IdSchema.nullable(),
    requestId: IdSchema,
    name: z.string().max(120),
    x: z.number(),
    z: z.number(),
    by: z.enum(["owner", "rule"]),
    /** The owner's reason, or a rule code (`overlap`, `outside_world`, ...). */
    reason: z.string().max(500),
});
/** Company-node routes that serve buildings' public facades to the world service (package 4). */
export const NODE_WORLD_API = {
    /** GET → `BuildingFacade` (node service token); trashed buildings too (`deleted: true`). */
    facade: "/v1/world/facade/:buildingId",
    /** GET → `BuildingFacadeList`: every building of the node, trashed ones too (reconcile sweep). */
    facades: "/v1/world/facades",
};
/**
 * The only building data that leaves a company node for the world: no agents, floor names or
 * projects (docs/PHASE2_PLAN.md §1, §2.5).
 */
export const BuildingFacadeSchema = z
    .object({
    buildingId: IdSchema,
    name: z.string().min(1).max(120),
    footprint: z.object({ width: z.number().positive(), depth: z.number().positive() }),
    /** Floor levels and themes only: floor names, agents and projects never leave the node. */
    floors: z.array(z.object({ level: z.number().int(), theme: FloorThemeSchema.optional() }).strict()),
    /** The company's public branding (logo, colors) for the sign, flag and lobby wall. */
    branding: CompanyBrandingSchema.optional(),
    /** The owner-chosen look of the building (preset, entrance, garage, flags, ...). */
    style: BuildingStyleSchema.optional(),
    deleted: z.boolean(),
    /** Node event seq of the building's latest change (sync idempotency key). */
    seq: z.number().int().nonnegative(),
})
    .strict();
/** `GET /v1/world/facades`. */
export const BuildingFacadeListSchema = z
    .object({ facades: z.array(BuildingFacadeSchema) })
    .strict();
/**
 * The minimal shape every scene node has (`@uzverse/world-scene` `SceneNodeSchema` is the full
 * schema). Gateways and fakes need only the id and version; other fields pass through.
 */
export const SceneNodeEnvelopeSchema = z.looseObject({
    id: IdSchema,
    version: z.number().int().positive(),
});
/** The minimal shape every scene op has (full schema: `SceneOpSchema` in world-scene). */
export const SceneOpEnvelopeSchema = z.discriminatedUnion("op", [
    z.looseObject({ op: z.literal("upsert"), node: SceneNodeEnvelopeSchema }),
    z.looseObject({
        op: z.literal("remove"),
        id: IdSchema,
        version: z.number().int().positive(),
    }),
]);
/** `GET /v1/scene/snapshot`: every live node at `worldSeq`. */
export const WorldSceneSnapshotSchema = z.object({
    worldSeq: z.number().int().nonnegative(),
    serverTime: TimestampSchema,
    nodes: z.array(SceneNodeEnvelopeSchema),
});
/**
 * One frame of the world scene stream.
 * - `ops`: the ops of world events up to and including `worldSeq`, in order (`ops` may be empty).
 * - `pose`: a live driving pose of one vehicle, relayed to subscribers without entering the op ring.
 * - `reset`: the requested `afterSeq` cannot be covered (older than the op ring, or newer than
 *   the head). The stream ends after it; the consumer takes a fresh snapshot.
 */
export const WorldStreamFrameSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("ops"),
        worldSeq: z.number().int().nonnegative(),
        ops: z.array(SceneOpEnvelopeSchema),
    }),
    z.object({ type: z.literal("reset"), headSeq: z.number().int().nonnegative() }),
    /** A driving pose (not a scene op, no `worldSeq`): the hub relays it as `vehicle.pose`. */
    z.object({ type: z.literal("pose"), pose: VehiclePoseMessageSchema }),
]);
//# sourceMappingURL=world-service.js.map